import { useCallback, useMemo, useRef } from 'react';

import { Country } from '@xemplo/country-helper';
import { XemploInputProps } from '@xemplo/xemplo-input';

import { formatDisplayValue, tryParsePhoneNumber } from '../helpers/phone-field.helper';
import { PhoneFieldProps } from '../phone-field.types';

type UsePhoneFieldEventsProps = {
  baseProps: PhoneFieldProps;
  inputPropsFactory: (otherProps?: Partial<XemploInputProps>) => PhoneFieldProps;
  selectedCountry: Country.Code;
};

const deletingKeys = ['Backspace', 'Delete'];

export function usePhoneFieldEvents(props: UsePhoneFieldEventsProps) {
  const { baseProps, inputPropsFactory, selectedCountry } = props;
  const { value, onChange, onKeyDown, onValueChange } = baseProps;
  const isDeleting = useRef(false);

  const displayValue = useMemo(
    () => formatDisplayValue(value, selectedCountry, isDeleting.current),
    [value, selectedCountry],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      isDeleting.current = deletingKeys.includes(event.key);
      onKeyDown?.(event);
    },
    [onKeyDown],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);

      if (!onValueChange) return;

      const rawValue = event.target.value.trim();
      const parsed = tryParsePhoneNumber(rawValue, selectedCountry);

      let emittingValue = parsed?.number ?? '';

      // The parser returns undefined for a single digit input.
      // Handle that case so RHF doesn't consider the field invalid/empty.
      if (!parsed && rawValue.length === 1) {
        emittingValue = `${Country.getPhoneCodeByCountryCode(selectedCountry)}${rawValue}`;
      }
      onValueChange(emittingValue);
    },
    [onChange, onValueChange, selectedCountry],
  );

  return inputPropsFactory({
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    value: displayValue,
  });
}
